/**
 * AgingReportWidget — vertical bar chart of $ outstanding bucketed by age
 * since invoice creation (<30, 30-59, 60-90, >90 days). Per the Penpot
 * mockup, has an OPEN / PARTIALLY PAID / PAID status toggle in the header.
 *
 * Bar colors come from the bootstrap catalog (display.barColors) so a
 * future admin UI can override per-business — fallback is the standard
 * green/amber/orange/red aging semaphore.
 */

import * as React from "react";
import { useQueryClient } from "react-query";
import { BarChartCard, RefreshButton, WidgetCard } from "components/dashboard";
import { useAgingReport, formatQueryError } from "../hooks";
import type {
  AgingBucketKey,
  InvoiceStatusFilter,
  LayoutItem,
  WidgetConfig,
} from "../types";

interface Props {
  widget: WidgetConfig;
  layout: LayoutItem;
}

const FALLBACK_COLORS: Record<AgingBucketKey, string> = {
  lte30: "34,197,94",   // green-500
  gt30: "163,230,53",   // lime-400
  gt60: "234,179,8",    // yellow-500
  gt90: "249,115,22",   // orange-500
  gte120: "239,68,68",  // red-500
};

// Status filter pill labels — full "Partially Paid" reads better here since
// the toggle lives on its own row (right-aligned under the header), so it
// no longer competes with the title for horizontal space.
const STATUS_LABELS: Record<InvoiceStatusFilter, string> = {
  open: "Open",
  partial: "Partially Paid",
  paid: "Paid",
};

export function AgingReportWidget({ widget, layout }: Props) {
  const queryClient = useQueryClient();

  const defaultStatus =
    (widget.display.defaultStatusFilter as InvoiceStatusFilter | undefined) ?? "open";
  const [status, setStatus] = React.useState<InvoiceStatusFilter>(defaultStatus);

  const { data, isLoading, error } = useAgingReport(status);

  const barColors =
    (widget.display.barColors as Partial<Record<AgingBucketKey, string>> | undefined) ?? {};

  const chartData = (data?.buckets ?? []).map((b) => ({
    label: b.label,
    value: b.amountCents,
    color: barColors[b.key] ?? FALLBACK_COLORS[b.key],
  }));

  return (
    <WidgetCard
      title={widget.title}
      w={widget.defaultSize.w}
      h={widget.defaultSize.h}
      x={layout.x}
      y={layout.y}
      loading={isLoading}
      error={error ? formatQueryError(error) : null}
      headerRight={
        <RefreshButton
          onClick={() => queryClient.invalidateQueries(["dashboard", "aging-report"])}
        />
      }
    >
      {/* Toggle gets its own row above the chart, right-aligned per design. */}
      <div className="flex justify-end mb-2">
        <StatusToggle value={status} onChange={setStatus} />
      </div>
      <BarChartCard
        data={chartData}
        yFormat="currencyCompact"
        height={180}
        minBarHeight={3}
      />
    </WidgetCard>
  );
}

function StatusToggle({
  value,
  onChange,
}: {
  value: InvoiceStatusFilter;
  onChange: (v: InvoiceStatusFilter) => void;
}) {
  return (
    <div className="inline-flex rounded-full bg-gray-100 p-0.5 text-[10px]">
      {(["open", "partial", "paid"] as const).map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(opt)}
          className={`px-2 py-0.5 rounded-full transition-colors ${
            value === opt
              ? "bg-white text-gray-700 shadow-sm"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          {STATUS_LABELS[opt]}
        </button>
      ))}
    </div>
  );
}
